import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, type PagedResult, type StructureListItem, type ProjectFolderListItem } from '../lib/api';
import { structureListDisplayName } from '../lib/structureReport';

/**
 * Admin dashboard — mirrors `.old/resources/views/dashboard/projects.blade.php`:
 * page title row + two card panels (My Projects / My Structures) with bordered tables.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const isSuper = !!user?.roles.includes('super-admin');
  const [showAll, setShowAll] = useState(false);

  const allParam = isSuper && showAll ? '&all=true' : '';

  const structures = useQuery({
    queryKey: ['structures', { all: showAll }],
    queryFn: () =>
      api.get<PagedResult<StructureListItem>>(`/api/v1/structures?pageSize=10${allParam}`),
  });

  const projects = useQuery({
    queryKey: ['project-folders', { all: showAll }],
    queryFn: () =>
      api.get<PagedResult<ProjectFolderListItem>>(`/api/v1/project-folders?pageSize=10${allParam}`),
  });

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          <div className="page-title-box d-flex justify-content-between align-items-end flex-wrap gap-2">
            <div>
              <h4 className="page-title mb-1">Welcome, {user?.name}</h4>
              <p className="text-muted small mb-0">{user?.roles.join(', ') || 'No roles'}</p>
            </div>
            {isSuper && (
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="show-all-toggle"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="show-all-toggle">
                  Show all users
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-xl-6">
          <Panel title="My Projects" total={projects.data?.total} addHref="/admin/projects">
            {projects.isLoading && <p className="text-muted mb-0">Loading…</p>}
            {projects.error && (
              <p className="text-danger mb-0">
                Failed: {projects.error instanceof Error ? projects.error.message : 'Unknown'}
              </p>
            )}
            {projects.data && (
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>City</th>
                      <th>State</th>
                      <th className="text-center">Structures</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.data.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-3">
                          No projects.
                        </td>
                      </tr>
                    )}
                    {projects.data.items.map((p) => (
                      <tr key={p.id}>
                        <td>{p.id}</td>
                        <td>
                          <Link to={`/admin/projects/${p.id}`}>{p.name}</Link>
                        </td>
                        <td>{p.city}</td>
                        <td>{p.state}</td>
                        <td className="text-center">{p.structureCount}</td>
                        <td>
                          {p.deliveredStatus ? (
                            <span className="badge bg-success">Delivered</span>
                          ) : p.completedStatus ? (
                            <span className="badge bg-info">Complete</span>
                          ) : (
                            <span className="badge bg-secondary">Draft</span>
                          )}
                        </td>
                        <td>{formatDate(p.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <div className="col-12 col-xl-6">
          <Panel title="My Structures" total={structures.data?.total} addHref="/admin/structures">
            {structures.isLoading && <p className="text-muted mb-0">Loading…</p>}
            {structures.error && (
              <p className="text-danger mb-0">
                Failed: {structures.error instanceof Error ? structures.error.message : 'Unknown'}
              </p>
            )}
            {structures.data && (
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nickname</th>
                      <th>Type</th>
                      <th>Grate Style</th>
                      <th>Foundry</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structures.data.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-3">
                          No structures.
                        </td>
                      </tr>
                    )}
                    {structures.data.items.map((s) => (
                      <tr key={s.id}>
                        <td>{s.id}</td>
                        <td>
                          {s.nickname ?? <span className="text-muted">{structureListDisplayName(s)}</span>}
                        </td>
                        <td>{s.structureTypeName ?? '—'}</td>
                        <td>{s.grateStyleName ?? '—'}</td>
                        <td>{s.foundryName ?? '—'}</td>
                        <td>
                          {s.isDraft ? (
                            <span className="badge bg-warning">Draft</span>
                          ) : s.isCompleted ? (
                            <span className="badge bg-success">Complete</span>
                          ) : (
                            <span className="badge bg-secondary">Open</span>
                          )}
                        </td>
                        <td>{formatDate(s.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({
  title,
  total,
  addHref,
  children,
}: {
  title: string;
  total?: number;
  addHref?: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="header-title mb-0">{title}</h4>
        <div className="d-flex align-items-center gap-2">
          {typeof total === 'number' && (
            <span className="text-muted small">{total} total</span>
          )}
          {addHref && (
            <Link to={addHref} className="btn btn-sm btn-outline-primary">
              View All
            </Link>
          )}
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
